/**
 * Collectible Entity
 *
 * PHASE 7A: Converted from Graphics to Sprites for massive performance gain
 *
 * Represents collectible items that can be picked up by the player.
 * Based on existing decoration types (fireflies, snails, frogs, etc.)
 * Items are spawned at decoration positions using distance-based collection.
 *
 * Performance:
 * - Before: Graphics redrawn every frame (CPU-intensive)
 * - After: Sprite with cached texture (GPU-accelerated)
 * - Expected gain: 15-25 FPS on mobile devices
 */

import Phaser from 'phaser';
import { CollectibleTextureGenerator } from '@/rendering/CollectibleTextureGenerator';

export enum CollectibleType {
  // Zone 1
  FIREFLY = 'firefly',
  SNAIL = 'tiny-snail',
  FROG = 'small-frog',
  BUTTERFLY = 'butterfly',
  BEETLE = 'beetle',
  DEWDROP = 'dewdrop',
  MUSHROOM = 'mushroom-buddy',
  // Zone 2
  BIRD = 'small-bird',
  MOTH = 'moth',
  // Zone 3
  FEATHER = 'floating-feather',
  // Zone 4
  STAR = 'twinkling-star',
  STARDUST = 'star-dust',
  // Zone 5
  COIN = 'gold-coin',
  GEM = 'gem-crystal',
  SPARKLE = 'golden-sparkle',
  // Zone 6
  ENERGY_ORB = 'energy-orb',
  COSMIC = 'cosmic-particle',
  PORTAL = 'portal-fragment',
}

export interface CollectibleConfig {
  type: CollectibleType;
  radius: number; // Collision radius
  points: number; // Score value (for future)
  animationSpeed: number; // Animation speed multiplier
  soundKey: string; // Sound effect identifier
}

export const COLLECTIBLE_CONFIGS: Record<CollectibleType, Omit<CollectibleConfig, 'type'>> = {
  // Zone 1
  [CollectibleType.FIREFLY]: {
    radius: 25,
    points: 10,
    animationSpeed: 1.5,
    soundKey: 'collect-firefly',
  },
  [CollectibleType.SNAIL]: {
    radius: 25,
    points: 5,
    animationSpeed: 0.5,
    soundKey: 'collect-snail',
  },
  [CollectibleType.FROG]: { radius: 25, points: 15, animationSpeed: 1.0, soundKey: 'collect-frog' },
  [CollectibleType.BUTTERFLY]: {
    radius: 25,
    points: 20,
    animationSpeed: 2.0,
    soundKey: 'collect-butterfly',
  },
  [CollectibleType.BEETLE]: {
    radius: 25,
    points: 10,
    animationSpeed: 0.8,
    soundKey: 'collect-beetle',
  },
  [CollectibleType.DEWDROP]: {
    radius: 25,
    points: 5,
    animationSpeed: 1.0,
    soundKey: 'collect-dewdrop',
  },
  [CollectibleType.MUSHROOM]: {
    radius: 25,
    points: 15,
    animationSpeed: 0.5,
    soundKey: 'collect-mushroom',
  },
  // Zone 2
  [CollectibleType.BIRD]: { radius: 25, points: 20, animationSpeed: 1.5, soundKey: 'collect-bird' },
  [CollectibleType.MOTH]: { radius: 25, points: 15, animationSpeed: 1.2, soundKey: 'collect-moth' },
  // Zone 3
  [CollectibleType.FEATHER]: {
    radius: 25,
    points: 12,
    animationSpeed: 0.8,
    soundKey: 'collect-feather',
  },
  // Zone 4
  [CollectibleType.STAR]: { radius: 25, points: 25, animationSpeed: 1.5, soundKey: 'collect-star' },
  [CollectibleType.STARDUST]: {
    radius: 25,
    points: 18,
    animationSpeed: 1.3,
    soundKey: 'collect-stardust',
  },
  // Zone 5
  [CollectibleType.COIN]: { radius: 25, points: 30, animationSpeed: 1.0, soundKey: 'collect-coin' },
  [CollectibleType.GEM]: { radius: 25, points: 50, animationSpeed: 0.8, soundKey: 'collect-gem' },
  [CollectibleType.SPARKLE]: {
    radius: 25,
    points: 22,
    animationSpeed: 1.2,
    soundKey: 'collect-sparkle',
  },
  // Zone 6
  [CollectibleType.ENERGY_ORB]: {
    radius: 25,
    points: 40,
    animationSpeed: 1.4,
    soundKey: 'collect-energy',
  },
  [CollectibleType.COSMIC]: {
    radius: 25,
    points: 45,
    animationSpeed: 1.5,
    soundKey: 'collect-cosmic',
  },
  [CollectibleType.PORTAL]: {
    radius: 25,
    points: 50,
    animationSpeed: 1.6,
    soundKey: 'collect-portal',
  },
};

export class Collectible {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private sprite: Phaser.GameObjects.Sprite; // PHASE 7A: Changed from Graphics to Sprite
  private sensor: MatterJS.BodyType;
  private animationPhase: number = 0;
  private animationSpeed: number;
  private isCollected: boolean = false;
  private needsPulsing: boolean; // Cached check for pulsing types

  // MEMORY LEAK FIX (v0.8.6): Track particle tweens for cleanup
  private particleTweens: Phaser.Tweens.Tween[] = [];

  public readonly id: string; // Unique ID for persistence
  public readonly type: CollectibleType;
  public readonly config: CollectibleConfig;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    type: CollectibleType,
    decorationIndex: number, // Used for unique ID generation
    platformId: number, // Used for unique ID generation
    customId?: string // Optional: Provide exact ID for deterministic spawning
  ) {
    this.scene = scene;
    this.type = type;
    this.config = {
      type,
      ...COLLECTIBLE_CONFIGS[type],
    };
    this.animationSpeed = this.config.animationSpeed;

    // Cache whether this type needs pulsing animation (for performance)
    this.needsPulsing =
      type === CollectibleType.FIREFLY ||
      type === CollectibleType.STAR ||
      type === CollectibleType.GEM ||
      type === CollectibleType.SPARKLE ||
      type === CollectibleType.ENERGY_ORB ||
      type === CollectibleType.STARDUST;

    // Use custom ID if provided (for deterministic spawning), otherwise generate from platform/decoration index
    this.id = customId || `p${platformId}_d${decorationIndex}_${type}`;

    // Create container for sprite
    this.container = scene.add.container(x, y);
    this.container.setDepth(100); // Well above platforms (1) and decorations to prevent overlap issues

    // PHASE 7A: Create sprite from pre-generated texture instead of Graphics
    // This eliminates CPU-intensive drawing every frame!
    const textureKey = CollectibleTextureGenerator.getTextureKey(this.type);
    this.sprite = scene.add.sprite(0, 0, textureKey);
    this.sprite.setOrigin(0.5, 0.5); // Center the sprite
    this.container.add(this.sprite);

    // Create and play animation from sprite sheet
    this.createAnimation();
    this.playAnimation();

    // NOTE: No physics sensor needed - we use distance-based collection in update loop
    // This eliminates 43 sensor bodies from physics world = huge performance improvement!
    // Create dummy sensor reference for compatibility (never added to world)
    this.sensor = null as any;
  }

  /**
   * Create animation from sprite sheet atlas
   */
  private createAnimation(): void {
    const animKey = `${this.type}_anim`;

    // Check if animation already exists (avoid duplicate creation)
    if (this.scene.anims.exists(animKey)) {
      return;
    }

    // Check if sprite sheet was loaded successfully
    const textureKey = CollectibleTextureGenerator.getTextureKey(this.type);
    if (!this.scene.textures.exists(textureKey)) {
      console.warn(`Sprite sheet not loaded for ${this.type}, skipping animation creation`);
      return;
    }

    try {
      // Dynamically detect actual frame count from texture atlas
      const texture = this.scene.textures.get(textureKey);
      const frameNames = texture.getFrameNames();

      // Filter frames that match this collectible type's prefix
      const collectibleFrames = frameNames.filter(name => name.startsWith(`${this.type}_`));

      // If no frames found with prefix, skip animation creation
      if (collectibleFrames.length === 0) {
        console.warn(`No frames found for ${this.type}, skipping animation creation`);
        return;
      }

      // Calculate the actual end frame number (frames are 0-indexed)
      const actualFrameCount = collectibleFrames.length;

      // Create animation from sprite sheet frames
      // Frames are generated with keys like: firefly_0, firefly_1, firefly_2, etc.
      this.scene.anims.create({
        key: animKey,
        frames: this.scene.anims.generateFrameNames(textureKey, {
          prefix: `${this.type}_`,
          start: 0,
          end: actualFrameCount - 1, // Use actual frame count from texture
        }),
        frameRate: 12 * this.animationSpeed, // Adjust frame rate by animation speed
        repeat: -1, // Loop forever
      });
    } catch (e) {
      console.warn(`Failed to create animation for ${this.type}:`, e);
    }
  }

  /**
   * Play the sprite sheet animation
   */
  private playAnimation(): void {
    const animKey = `${this.type}_anim`;

    // Only play if animation exists
    if (this.scene.anims.exists(animKey)) {
      try {
        this.sprite.play(animKey);
      } catch (e) {
        console.warn(`Failed to play animation for ${this.type}:`, e);
      }
    }
  }

  /**
   * Update animation (called from GameScene update)
   * PHASE 7A: Optimized to use sprite transforms (GPU-accelerated)
   */
  public update(deltaTime: number): void {
    if (this.isCollected) return;

    // Comprehensive safety checks
    if (!this.scene || !this.sprite) return;
    if (!this.scene.sys || !this.scene.sys.game) return;

    try {
      this.animationPhase += deltaTime * this.animationSpeed * 0.001;

      // PHASE 7A: Gentle floating animation using sprite transform (GPU-accelerated)
      const floatOffset = Math.sin(this.animationPhase * 2) * 3;
      this.sprite.y = floatOffset;

      // PHASE 7A: Gentle pulsing for glowing types using sprite.setScale (GPU-accelerated)
      // Uses cached property for performance
      if (this.needsPulsing) {
        const pulseScale = 1 + Math.sin(this.animationPhase * 4) * 0.1;
        this.sprite.setScale(pulseScale);
      }
    } catch (e) {
      // Silently ignore errors if scene is being destroyed
    }
  }

  /**
   * Collect this item (play animation and mark as collected)
   */
  public collect(): void {
    if (this.isCollected) return;

    this.isCollected = true;

    // NOTE: No sensor to remove - we use distance-based collection now

    // Comprehensive safety checks before using tweens
    const sceneValid = this.scene && this.scene.sys && this.scene.sys.game && this.scene.tweens;

    // Play collection animation (fade out + scale up)
    if (sceneValid && this.container) {
      try {
        this.scene.tweens.add({
          targets: this.container,
          alpha: 0,
          scale: 1.5,
          duration: 300,
          ease: 'Power2',
          onComplete: () => {
            this.destroy();
          },
        });

        // Emit particle burst at collection point
        this.emitCollectionParticles();
      } catch (e) {
        // If tween creation fails, destroy immediately
        this.destroy();
      }
    } else {
      // If scene is invalid, just destroy immediately
      this.destroy();
    }
  }

  /**
   * Emit particle effect when collected
   */
  private emitCollectionParticles(): void {
    // Comprehensive safety checks
    const sceneValid =
      this.scene &&
      this.scene.sys &&
      this.scene.sys.game &&
      this.scene.add &&
      this.scene.tweens &&
      this.container;

    if (!sceneValid) return;

    try {
      // Reduced particle count for better performance (was 8, now 4)
      const particleCount = 4;

      for (let i = 0; i < particleCount; i++) {
        const angle = (i / particleCount) * Math.PI * 2;
        const speed = 60 + Math.random() * 40;
        const vx = Math.cos(angle) * speed;
        const vy = Math.sin(angle) * speed;

        const particle = this.scene.add.circle(
          this.container.x,
          this.container.y,
          3,
          this.getTypeColor(),
          0.8
        );
        particle.setDepth(100);

        // Shorter duration for snappier feel and better performance
        // MEMORY LEAK FIX (v0.8.6): Store tween for cleanup in case scene destroys early
        const tween = this.scene.tweens.add({
          targets: particle,
          x: particle.x + vx,
          y: particle.y + vy,
          alpha: 0,
          scale: 0,
          duration: 350,
          ease: 'Quad.easeOut',
          onComplete: () => {
            try {
              if (particle && particle.scene) {
                particle.destroy();
              }
            } catch (e) {
              // Ignore if already destroyed
            }
            // Remove tween from tracking array after completion
            const index = this.particleTweens.indexOf(tween);
            if (index > -1) {
              this.particleTweens.splice(index, 1);
            }
          },
        });
        this.particleTweens.push(tween);
      }
    } catch (e) {
      // Silently ignore if scene is being destroyed
    }
  }

  /**
   * Get color based on collectible type
   */
  private getTypeColor(): number {
    switch (this.type) {
      case CollectibleType.FIREFLY:
        return 0xffff00;
      case CollectibleType.SNAIL:
        return 0x8b4513;
      case CollectibleType.FROG:
        return 0x00ff00;
      case CollectibleType.BUTTERFLY:
        return 0xff1493;
      case CollectibleType.MOTH:
        return 0x9370db;
      case CollectibleType.GEM:
        return 0x00ffff;
      case CollectibleType.COIN:
        return 0xffd700;
      case CollectibleType.STAR:
        return 0xffff00;
      case CollectibleType.BEETLE:
        return 0x000000;
      case CollectibleType.BIRD:
        return 0x4169e1;
      case CollectibleType.DEWDROP:
        return 0x87ceeb;
      case CollectibleType.MUSHROOM:
        return 0xff0000;
      case CollectibleType.FEATHER:
        return 0xf0f0f0;
      case CollectibleType.STARDUST:
        return 0xffd700;
      case CollectibleType.SPARKLE:
        return 0xffd700;
      case CollectibleType.ENERGY_ORB:
        return 0x00ffff;
      case CollectibleType.COSMIC:
        return 0xff00ff;
      case CollectibleType.PORTAL:
        return 0x8a2be2;
      default:
        return 0xffffff;
    }
  }

  /**
   * Check if already collected
   */
  public isAlreadyCollected(): boolean {
    return this.isCollected;
  }

  /**
   * Get position
   */
  public getPosition(): { x: number; y: number } {
    return {
      x: this.container.x,
      y: this.container.y,
    };
  }

  /**
   * Get bounds for frustum culling
   */
  public getBounds(): { x: number; y: number; width: number; height: number } {
    const radius = this.config.radius;
    return {
      x: this.container.x - radius,
      y: this.container.y - radius,
      width: radius * 2,
      height: radius * 2,
    };
  }

  /**
   * Get collision sensor
   */
  public getSensor(): MatterJS.BodyType {
    return this.sensor;
  }

  /**
   * Clean up
   * PHASE 7A: Destroy sprite instead of graphics
   * MEMORY LEAK FIX (v0.8.6): Stop particle tweens before destroy
   */
  public destroy(): void {
    // MEMORY LEAK FIX: Stop all particle tweens if scene destroys early
    // Without this, tweens and particles leak if scene transitions before completion
    if (this.particleTweens.length > 0) {
      this.particleTweens.forEach(tween => {
        try {
          if (tween) {
            tween.stop();
          }
        } catch (e) {
          // Ignore if already stopped
        }
      });
      this.particleTweens = [];
    }

    // NOTE: No physics sensor to remove - we use distance-based collection
    if (this.sprite) {
      try {
        this.sprite.destroy();
      } catch (e) {
        // Ignore errors if already destroyed
      }
    }
    if (this.container) {
      try {
        this.container.destroy();
      } catch (e) {
        // Ignore errors if already destroyed
      }
    }
  }
}
